/**
 * Export notițe ca ZIP cu markdown local (client-side, zero server-side
 * decrypt). Fiecare notiță decriptată local, convertită HTML → MD,
 * assets decriptate și incluse în `_assets/`. Structură arborescentă
 * mirror.
 */
import JSZip from 'jszip';
import TurndownService from 'turndown';

import { assetsApi } from '@/api/assets';
import { notesApi, type NoteSummary } from '@/api/notes';
import { bytesToStr, decryptWithWrappedKey, type KeyPair } from '@/crypto/sodium';
import i18n from '@/i18n';

const MIME_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

interface TreeNode extends NoteSummary {
  children: TreeNode[];
}

function buildTree(flat: NoteSummary[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  flat.forEach((n) => byId.set(n.id, { ...n, children: [] }));
  const roots: TreeNode[] = [];
  for (const n of byId.values()) {
    if (n.parent_id && byId.has(n.parent_id)) {
      byId.get(n.parent_id)!.children.push(n);
    } else {
      roots.push(n);
    }
  }
  return roots;
}

// Sanitizare nume folder/fișier — permisiv dar evită `/ \ : * ? " < > |`.
function safeFileName(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, '_').trim() || 'untitled';
}

/**
 * Transformă `<img data-asset-id="ID" alt="name">` și `<a data-note-mention
 * data-note-id="ID">title</a>` în echivalente markdown înainte de turndown.
 * Colectează asset IDs pentru a le include în ZIP.
 */
function preprocessHtml(
  html: string,
  assetFileMap: Map<string, string>,
): { html: string; referencedAssetIds: string[] } {
  const referenced: string[] = [];
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll<HTMLImageElement>('img[data-asset-id]').forEach((img) => {
    const id = img.getAttribute('data-asset-id') ?? '';
    const alt = img.getAttribute('alt') ?? 'image';
    referenced.push(id);
    const fileName = assetFileMap.get(id) ?? `${id}`;
    const replacement = doc.createElement('img');
    replacement.setAttribute('src', `_assets/${fileName}`);
    replacement.setAttribute('alt', alt);
    img.replaceWith(replacement);
  });

  doc.querySelectorAll<HTMLAnchorElement>('a[data-note-mention]').forEach((a) => {
    const title = a.textContent || 'note';
    // În markdown lăsăm un link „decorativ" (nu rezolvăm path-uri aici —
    // dacă userul reimportă markdown undeva, referința e informativă).
    const replacement = doc.createElement('a');
    replacement.textContent = `📄 ${title}`;
    replacement.setAttribute('href', '#');
    a.replaceWith(replacement);
  });

  return { html: doc.body.innerHTML, referencedAssetIds: referenced };
}

/**
 * Construiește calea folder în ZIP: lanțul de parent slugs + titlul sanitizat.
 */
function buildZipPath(node: TreeNode, parents: TreeNode[]): string {
  const parentsPath = parents.map((p) => safeFileName(p.title)).join('/');
  const fileName = safeFileName(node.title) + '.md';
  return parentsPath ? `${parentsPath}/${fileName}` : fileName;
}

interface ExportProgress {
  total: number;
  done: number;
  currentLabel: string;
}

export interface ExportOptions {
  tenantName: string;
  notes: NoteSummary[];
  keyPair: KeyPair;
  onProgress?: (p: ExportProgress) => void;
}

export async function exportNotesToZip(opts: ExportOptions): Promise<Blob> {
  const { tenantName, notes, keyPair, onProgress } = opts;

  const tree = buildTree(notes);
  const zip = new JSZip();
  const root = zip.folder(safeFileName(tenantName) ?? 'notes')!;
  const assetsFolder = root.folder('_assets')!;

  const turndown = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
  });
  turndown.keep(['blockquote']);

  // Prima trecere: fetch meta pentru assets ca să știm filename-urile.
  // Map asset_id → fișier final (numele sanitizat + ID prefix ca să evităm colizii).
  const assetFileMap = new Map<string, string>();
  const collectedAssetIds = new Set<string>();

  let done = 0;
  const total = notes.length;

  // Colectăm în ordine arborescentă (BFS) ca să avem assets mapate în
  // momentul convertirii.
  const walkFirstPass = async (node: TreeNode): Promise<void> => {
    onProgress?.({ total, done, currentLabel: i18n.t('notes_ui.export.progress_meta', { title: node.title }) });
    const assets = await assetsApi.list(node.id);
    for (const a of assets) {
      const ext = MIME_EXT[a.mime_type] ?? 'bin';
      const safeFilename = safeFileName(a.filename).replace(/\.[^.]+$/, '');
      assetFileMap.set(a.id, `${a.id.slice(0, 8)}-${safeFilename}.${ext}`);
      collectedAssetIds.add(a.id);
    }
    for (const c of node.children) await walkFirstPass(c);
  };
  for (const r of tree) await walkFirstPass(r);

  // A doua trecere: fetch + decrypt fiecare notiță → markdown.
  const walkEmit = async (node: TreeNode, parents: TreeNode[]): Promise<void> => {
    done += 1;
    onProgress?.({ total, done, currentLabel: node.title });

    const detail = await notesApi.get(node.id);
    const plainBytes = await decryptWithWrappedKey(detail, keyPair);
    const html = bytesToStr(plainBytes);
    const { html: preprocessed, referencedAssetIds } = preprocessHtml(
      html,
      assetFileMap,
    );
    const md = turndown.turndown(preprocessed);

    const frontMatter = [
      '---',
      `title: ${JSON.stringify(node.title)}`,
      `slug: ${node.slug}`,
      `id: ${node.id}`,
      `version: ${node.current_version}`,
      `updated_at: ${node.updated_at}`,
      '---',
      '',
    ].join('\n');

    const zipPath = buildZipPath(node, parents);
    root.file(zipPath, frontMatter + md);

    // Referențează asset ids pentru decriptare în a treia trecere (colectate
    // global — evită duplicate fetch dacă același asset apare în mai multe notițe,
    // deși atașate unei singure notițe în modelul curent).
    for (const id of referencedAssetIds) collectedAssetIds.add(id);

    for (const c of node.children) await walkEmit(c, [...parents, node]);
  };
  for (const r of tree) await walkEmit(r, []);

  // A treia trecere: decrypt fiecare asset.
  let assetsDone = 0;
  for (const assetId of collectedAssetIds) {
    assetsDone += 1;
    onProgress?.({
      total: collectedAssetIds.size,
      done: assetsDone,
      currentLabel: i18n.t('notes_ui.export.progress_asset', {
        name: assetFileMap.get(assetId) ?? assetId,
      }),
    });
    try {
      const a = await assetsApi.get(assetId);
      const bytes = await decryptWithWrappedKey(a, keyPair);
      const name = assetFileMap.get(assetId) ?? assetId;
      assetsFolder.file(name, bytes as unknown as Uint8Array);
    } catch {
      // Asset indisponibil (ex. user n-a fost în share) — scriem un stub.
      const name = assetFileMap.get(assetId) ?? assetId;
      assetsFolder.file(`${name}.missing.txt`, `Asset ${assetId} inaccesibil`);
    }
  }

  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
